import 'bootstrap/dist/css/bootstrap.css';
import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import ReactInputMask from 'react-input-mask';
import Spinner from '../../../shared/spinner';
import CustomAlert from '../../../shared/customAlert';
import BigDataService from '../../../services/bigDataService';

interface PepDados {
  nomePEP?: string;
  cpf?: string;
  nomeOrgao?: string;
  descricaoFuncao?: string;
  siglaFuncao?: string;
  nivelFuncao?: string;
  dataInicioExercicio?: string;
  dataFimExercicio?: string;
  dataFimCarencia?: string;
  competencia?: string;
}

interface PepResultado {
  status?: string;
  mensagem?: string;
  dados?: PepDados[];
}

function ConsultaPEPComponent() {
  const [cpf, setCpf] = useState('');
  const [idProposta, setIdProposta] = useState('');
  const [resultado, setResultado] = useState<PepResultado | null>(null);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState<{ message: string, type: 'success' | 'warning' | 'error' } | null>(null);

  const bigDataService = new BigDataService();

  const getUsuarioId = (): string => {
    return localStorage.getItem('id')
      || sessionStorage.getItem('id')
      || '';
  };

  const formatarData = (valor?: string): string => {
    if (!valor || valor.trim() === '') {
      return 'N/A';
    }
    const digits = valor.replace(/\D/g, '');
    if (/^\d{10,13}$/.test(digits)) {
      const epoch = parseInt(digits, 10);
      const date = digits.length <= 10 ? new Date(epoch * 1000) : new Date(epoch);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' });
      }
    }
    return valor;
  };

  const handleSearch = async () => {
    const cpfClean = cpf.replace(/\D/g, '');

    if (cpfClean.length !== 11) {
      setAlert({ message: 'Informe um CPF válido.', type: 'warning' });
      return;
    }

    const idusuario = getUsuarioId();
    if (!idusuario) {
      setAlert({ message: 'Usuário não identificado. Faça login novamente.', type: 'error' });
      return;
    }

    setLoading(true);
    setResultado(null);
    setAlert(null);

    try {
      const response = await bigDataService.consultaSincrona(
        cpfClean,
        idProposta || null,
        idusuario,
        'PEP_CHECKUP'
      );

      setResultado(response || {});
      setAlert({ message: 'Consulta PEP realizada com sucesso!', type: 'success' });
    } catch (error) {
      const errorMessage = (error as any).response?.data || (error as Error).message || 'Ocorreu um erro desconhecido.';
      setAlert({ message: `Erro na consulta: ${errorMessage}`, type: 'error' });
      console.error('Erro na consulta PEP:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {alert && <CustomAlert message={alert.message} type={alert.type} onClose={() => setAlert(null)} />}
      <div className="container-fluid mt-3">
        <div className="card">
          <div className="card-header bg-light"><h5 className="mb-0">Consulta PEP</h5></div>
          <div className="card-body">
            <div className="row g-3 align-items-end mb-3">
              <div className="col-md-4">
                <label className="form-label">CPF <span className="text-danger">*</span></label>
                <ReactInputMask
                  className="form-control"
                  mask="999.999.999-99"
                  placeholder="Digite o CPF"
                  value={cpf}
                  onChange={(e) => setCpf(e.target.value)}
                />
              </div>

              <div className="col-md-4">
                <label className="form-label">ID da Proposta</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Opcional — para salvar no dossiê"
                  value={idProposta}
                  onChange={(e) => setIdProposta(e.target.value.replace(/\D/g, ''))}
                />
              </div>
            </div>

            <div className="row">
              <div className="col-12">
                <button className="btn btn-primary" onClick={handleSearch} disabled={loading}>
                  <FontAwesomeIcon icon={faSearch} className="me-2" />
                  {loading ? 'Consultando...' : 'Buscar'}
                </button>
              </div>
            </div>

            {loading && <Spinner loading={loading} />}

            {!loading && resultado && (
              <div className="mt-4">
                <h6>Resultado</h6>
                <table className="table table-bordered table-sm">
                  <tbody>
                    <tr>
                      <th style={{ width: '30%' }}>Status</th>
                      <td>{resultado.status || 'N/A'}</td>
                    </tr>
                    <tr>
                      <th>Mensagem</th>
                      <td>{resultado.mensagem || 'N/A'}</td>
                    </tr>
                    {resultado.dados && resultado.dados.length > 0 && (
                      <tr>
                        <th>Consta na base PEP?</th>
                        <td className="text-danger fw-bold">SIM</td>
                      </tr>
                    )}
                  </tbody>
                </table>

                {resultado.dados && resultado.dados.map((dado, index) => (
                  <div key={index} className="card mb-3">
                    <div className="card-header bg-light"><h6 className="mb-0">Registro PEP {index + 1}</h6></div>
                    <div className="card-body">
                      <table className="table table-bordered table-sm">
                        <tbody>
                          <tr><th style={{ width: '30%' }}>Nome</th><td>{dado.nomePEP || 'N/A'}</td></tr>
                          <tr><th>CPF</th><td>{dado.cpf || 'N/A'}</td></tr>
                          <tr><th>Órgão</th><td>{dado.nomeOrgao || 'N/A'}</td></tr>
                          <tr><th>Função</th><td>{dado.descricaoFuncao || 'N/A'}</td></tr>
                          <tr><th>Sigla Função</th><td>{dado.siglaFuncao || 'N/A'}</td></tr>
                          <tr><th>Nível Função</th><td>{dado.nivelFuncao || 'N/A'}</td></tr>
                          <tr><th>Início Exercício</th><td>{formatarData(dado.dataInicioExercicio)}</td></tr>
                          <tr><th>Fim Exercício</th><td>{formatarData(dado.dataFimExercicio)}</td></tr>
                          <tr><th>Fim Carência</th><td>{formatarData(dado.dataFimCarencia)}</td></tr>
                          <tr><th>Competência</th><td>{formatarData(dado.competencia)}</td></tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default ConsultaPEPComponent;
